/* eslint-disable @next/next/no-sync-scripts */
import URLogoWhite from 'assets/icons/brand-ur-logo-white.svg';
import programAdapter from 'backend/lib/adapters/programAdapter';
import trailerAdapter from 'backend/lib/adapters/trailerAdapter';
import { CustomError } from 'backend/lib/clients/httpClient';
import { getServerFeatureFlags } from 'backend/lib/featureFlags/featureFlagService';
import embedService from 'backend/services/embedService';
import { StreamingInfo } from 'backend/services/types/urApiService.types';
import urApiService from 'backend/services/urApiService';
import OgMetadata from 'components/OgMetadata/OgMetadata';
import EmbeddedProductNotExistingError from 'components/Player/components/EmbeddedProductNotExistingError/EmbeddedProductNotExistingError';
import NotEmbeddableError from 'components/Player/components/NotEmbeddableError/NotEmbeddableError';
import Player from 'components/Player/Player';
import { ROOT_ELEMENT_ID } from 'components/UrPlayer/plugins/KeyboardControls/KeyboardControls';
import { DataLayerPageType, ProductType } from 'frontend/enums';
import { DEFAULT_CACHE_CONTROL } from 'frontend/lib/constants';
import { AccessiblePlatforms, Program, Trailer } from 'frontend/types';
import { EMBED_DOMAIN_NAME, objectIsEmpty } from 'frontend/utils/helpers';
import usePageTracking from 'frontend/utils/hooks/usePageTracking';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useState } from 'react';
import styles from 'styles/EmbedPage.module.css';
import productUtils from 'utils/productUtils';
const CIDS = ['dkrati'];
const SKINS = ['no_watermark', 'droger', 'fatta'];
const WHITELISTED_HOSTS = ['ur.se', 'urskola.se', 'udo-accept.ur.se', 'udo-prod.ur.se'];
interface Props {
  program: Program;
  isValidEmbed: boolean;
  hideWatermark: boolean;
  notExistingError: boolean;
  seoTitle: string;
  pageTitle: string;
}
const EmbedPage = ({
  program,
  isValidEmbed,
  hideWatermark,
  notExistingError,
  seoTitle,
  pageTitle
}: Props) => {
  usePageTracking(DataLayerPageType.EMBED_PAGE);
  const [player, setPlayer] = useState<jwplayer.JWPlayer | null>(null);
  if (notExistingError) return <>
        <Head>
          <title>Programmet finns inte | UR Play</title>
        </Head>
        <EmbeddedProductNotExistingError />
      </>;
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
        <title>{seoTitle}</title>
        <meta name="description" content={program.seoDescription || program.description} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <link rel="canonical" href={`https://urplay.se/id/${program.slug}`} />
        <script src="/nielsen/nielsen.min.js" />
      </Head>
      <OgMetadata title={`${pageTitle} | UR Play`} description={program.seoDescription || program.description} sharePath={`https://${EMBED_DOMAIN_NAME}/embed/${program.id}`} image={program.image['1280x720']} data-sentry-element="OgMetadata" data-sentry-source-file="index.tsx" />
      <main aria-label="inbäddad videouppspelare">
        {!hideWatermark && <a href={`https://urplay.se/program/${program.slug}`} target="_blank" rel="noreferrer" title="Se programmet på UR Play" className={styles.watermark} data-testid="watermark-title">
            <URLogoWhite className={styles.watermarkLogo} alt="UR - Kunskapsbanken" />
            {`${pageTitle} | UR Play`}
          </a>}

        {isValidEmbed ? <div id={ROOT_ELEMENT_ID} className="embedPlayer">
            <figure data-testid="player-container">
              <Player isEmbedded currentProduct={program} isAudio={false} setPlayer={setPlayer} player={player} />
            </figure>
          </div> : <div className={styles.notAccessibleContainer}>
            <NotEmbeddableError currentProgram={program} />
          </div>}
      </main>
    </>;
};
const isEmbedAccessible = (accessiblePlatforms: AccessiblePlatforms, embedStreamingInfo?: StreamingInfo | null) => {
  if (!embedStreamingInfo) return false;
  const now = new Date();
  const startTime = new Date(accessiblePlatforms.embed?.startTime ?? '');
  const endTime = new Date(accessiblePlatforms.embed?.endTime ?? '');
  return startTime <= now && endTime >= now;
};
export const getServerSideProps: GetServerSideProps = async ctx => {
  const {
    params,
    query,
    req,
    res
  } = ctx;
  let program: Program | Trailer;
  const id = (params?.id as string).split('-')[0];
  try {
    const {
      product
    } = await urApiService.getProduct(id);
    switch (product.productType) {
      case ProductType.PROGRAM:
        program = programAdapter.adaptV1(product);
        break;
      case ProductType.TRAILER:
        program = trailerAdapter.adapt(product);
        break;
      default:
        return {
          notFound: true
        };
    }
    if (objectIsEmpty(program.accessiblePlatforms)) {
      return {
        notFound: true
      };
    }
  } catch (error) {
    if ((error as CustomError).response?.statusCode === 404) {
      res.statusCode = 404;
      return {
        props: {
          notExistingError: true
        }
      };
    }
    throw new Error(error as string);
  }
  const isStreamable = isEmbedAccessible(program.accessiblePlatforms, program.embedStreamingInfo);
  const hasValidCid = embedService.authorize(id, query.cid as string);
  let host;
  try {
    host = new URL(encodeURI(req.headers.referer || '')).host;
  } catch {
    //  Silently fail
  }
  const isInternalSite = WHITELISTED_HOSTS.includes(host);
  const isValidEmbed = isStreamable && (isInternalSite || hasValidCid);
  const hideWatermark = program.productType === 'trailer' || CIDS.includes(query.cid as string) || SKINS.includes(query.skin as string);
  const seoTitle = `${isValidEmbed ? productUtils.getPageTitle(program, false, true) : 'Programmet går inte att bädda in'} | UR Play`;
  const pageTitle = productUtils.getPageTitle(program, false, false);
  ctx.res.setHeader('Cache-Control', DEFAULT_CACHE_CONTROL);
  return {
    props: {
      program,
      isValidEmbed,
      hideWatermark,
      seoTitle,
      pageTitle,
      featureFlags: getServerFeatureFlags()
    }
  };
};
export default EmbedPage;